import { api } from "./api";

export interface TrackResult {
  id: string;
  spotifyId: string;
  uri: string;
  title: string;
  name: string;
  artist: string;
  album: string | null;
  artworkUrl: string | null;
  coverUrl: string | null;
  durationMs: number;
  source: "local" | "spotify";
}

interface CacheValue {
  expiresAt: number;
  tracks: TrackResult[];
}

const MAX_CACHE_SIZE = 100;
const CACHE_TTL_MS = 5 * 60_000;
const queryCache = new Map<string, CacheValue>();

function readCache(query: string) {
  const key = query.toLowerCase();
  const value = queryCache.get(key);
  if (!value || value.expiresAt <= Date.now()) {
    queryCache.delete(key);
    return null;
  }
  queryCache.delete(key);
  queryCache.set(key, value);
  return value.tracks;
}

function writeCache(query: string, tracks: TrackResult[]) {
  const key = query.toLowerCase();
  queryCache.delete(key);
  queryCache.set(key, { tracks, expiresAt: Date.now() + CACHE_TTL_MS });
  while (queryCache.size > MAX_CACHE_SIZE) {
    const oldest = queryCache.keys().next().value;
    if (!oldest) break;
    queryCache.delete(oldest);
  }
}

function merge(local: TrackResult[], remote: TrackResult[]) {
  const found = new Set<string>();
  return [...local, ...remote].filter((track) => {
    if (found.has(track.spotifyId)) return false;
    found.add(track.spotifyId);
    return true;
  });
}

export function cachedTracks(query: string) {
  return readCache(query);
}

export async function searchLocalTracks(query: string, signal?: AbortSignal) {
  const payload = await api<{ results: TrackResult[] }>(`/api/tracks/search?${new URLSearchParams({ q: query })}`, { signal });
  writeCache(query, payload.results);
  return payload.results;
}

export async function searchRemoteTracks(query: string, local: TrackResult[], signal?: AbortSignal) {
  const payload = await api<{ tracks: TrackResult[] }>(`/api/spotify/search?${new URLSearchParams({ q: query })}`, { signal });
  const tracks = merge(local, payload.tracks || []);
  writeCache(query, tracks);
  return tracks;
}
